import React from 'react';
import { motion } from 'framer-motion';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import {useDocTitle} from '../components/CustomHook';
import { Link } from 'react-router-dom';
import portfolioData from '../data/portfolioData';
import SEO from '../components/SEO';

const Portfolio = () => {
    useDocTitle('Tomorrow Global - Portfolio')

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    return (
        <>
            <SEO 
                title="Portfolio - Tomorrow Global | Campagnes Publicitaires & Productions"
                description="Découvrez nos campagnes publicitaires, productions audiovisuelles et stratégies de communication qui ont créé un Impact 2.0 pour nos clients. Campagnes TV, Digital, Événementielle et Influence Marketing."
                keywords="portfolio campagnes publicitaires, productions audiovisuelles, exemples campagnes, cas clients, campagnes TV, marketing digital, influence marketing, événementielle, ROI campagnes"
            />
            <div>
                <NavBar />
            </div>
            
            <main className="min-h-screen bg-white dark:bg-black pt-24 md:pt-28">
                <div className="container mx-auto px-4 md:px-8 lg:px-20">
                    {/* Back Button */}
                    <motion.nav 
                        className="mb-6 md:mb-8 mt-4" 
                        aria-label="Navigation secondaire"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link 
                            to="/" 
                            className="inline-flex items-center text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-semibold transition duration-300 text-sm md:text-base"
                            aria-label="Retour à la page d'accueil"
                        >
                            <motion.svg 
                                className="w-4 h-4 md:w-5 md:h-5 mr-2" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24" 
                                aria-hidden="true"
                                whileHover={{ x: -5 }}
                                transition={{ duration: 0.2 }}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </motion.svg>
                            Retour à l'accueil
                        </Link>
                    </motion.nav>

                    {/* Hero Section */}
                    <motion.header 
                        className="text-center mb-12 md:mb-16"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <motion.h1 
                            className="text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 md:mb-6 px-2"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            Notre Portfolio
                        </motion.h1>
                        <motion.p 
                            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        >
                            Découvrez nos campagnes publicitaires, productions audiovisuelles et stratégies de communication qui ont créé un Impact 2.0 pour nos clients.
                        </motion.p>
                    </motion.header>

                    {/* Portfolio Projects */}
                    <motion.section 
                        className="space-y-16 md:space-y-24 mb-12 md:mb-16" 
                        itemScope 
                        itemType="https://schema.org/ItemList"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                    >
                        {portfolioData.map((project, index) => (
                            <motion.article 
                                key={project.id}
                                itemScope
                                itemType="https://schema.org/CreativeWork"
                                className={`flex flex-col lg:flex-row items-center gap-8 md:gap-12 ${
                                    project.alignment === 'right' ? 'lg:flex-row-reverse' : ''
                                }`}
                                variants={itemVariants}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                initial={{ opacity: 0, y: 50, x: project.alignment === 'left' ? -50 : 50 }}
                            >
                                {/* Content */}
                                <motion.div 
                                    className="flex-1 space-y-4 md:space-y-6 w-full"
                                    whileHover={{ x: project.alignment === 'right' ? -10 : 10 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <motion.div 
                                        className="text-4xl md:text-6xl mb-3 md:mb-4 text-center lg:text-left"
                                        whileHover={{ scale: 1.2, rotate: 5 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {project.icon}
                                    </motion.div>
                                    <motion.h2 
                                        className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white text-center lg:text-left"
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.2 }}
                                    >
                                        {project.title}
                                    </motion.h2>
                                    <motion.p 
                                        className="text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed text-center lg:text-left"
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                    >
                                        {project.description}
                                    </motion.p>
                                    
                                    {/* Features */}
                                    <motion.div 
                                        className="mt-6 md:mt-8"
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.4 }}
                                    >
                                        <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-3 md:mb-4 text-center lg:text-left">Fonctionnalités principales :</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                                            {project.features.map((feature, featureIndex) => (
                                                <motion.div 
                                                    key={featureIndex} 
                                                    className="flex items-start"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    whileInView={{ opacity: 1, x: 0 }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.4, delay: 0.5 + featureIndex * 0.1 }}
                                                >
                                                    <span className="text-gray-700 dark:text-gray-300 mr-3 mt-1 text-sm md:text-base">✓</span>
                                                    <span className="text-gray-700 dark:text-gray-300 text-sm md:text-base">{feature}</span>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>

                                    {/* Supports */}
                                    <motion.div 
                                        className="mt-6 md:mt-8"
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.6 }}
                                    >
                                        <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-3 md:mb-4 text-center lg:text-left">Supports & Services :</h3>
                                        <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                                            {project.supports.map((support, supportIndex) => (
                                                <motion.span 
                                                    key={supportIndex}
                                                    className="bg-gray-200 dark:bg-[#171717] text-gray-800 dark:text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium"
                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                    whileInView={{ opacity: 1, scale: 1 }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.3, delay: 0.7 + supportIndex * 0.05 }}
                                                    whileHover={{ scale: 1.1 }}
                                                >
                                                    {support}
                                                </motion.span>
                                            ))}
                                        </div>
                                    </motion.div>

                                    {/* CTA Buttons */}
                                    <motion.div 
                                        className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4 md:pt-6"
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.8 }}
                                    >
                                        <Link 
                                            to="/contact" 
                                            className="bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 text-white font-bold py-3 md:py-3 px-4 md:px-6 rounded-xl text-center transition duration-300 text-sm md:text-base"
                                        >
                                            En savoir plus
                                        </Link>
                                    </motion.div>
                                </motion.div>

                                {/* Image */}
                                <motion.div 
                                    className="flex-1 w-full"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <motion.div 
                                        className="relative rounded-2xl overflow-hidden h-64 md:h-96 shadow-xl"
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                    >
                                        <motion.img 
                                            src={`https://picsum.photos/seed/${project.imageId || project.id}/1920/1280`}
                                            alt={project.title}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            srcSet={`https://picsum.photos/seed/${project.imageId || project.id}/1920/1280 1920w, https://picsum.photos/seed/${project.imageId || project.id}/1280/853 1280w, https://picsum.photos/seed/${project.imageId || project.id}/800/533 800w`}
                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.5 }}
                                        />
                                    </motion.div>
                                </motion.div>
                            </motion.article>
                        ))}
                    </motion.section>

                    {/* CTA Section */}
                    <motion.div 
                        className="bg-gray-900 dark:bg-white rounded-2xl p-6 md:p-8 text-center text-white dark:text-gray-900 mb-12 md:mb-16"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h2 
                            className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 px-4"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            Prêt à créer l'Impact 2.0 pour votre marque ?
                        </motion.h2>
                        <motion.p 
                            className="text-lg md:text-xl mb-6 md:mb-8 opacity-90 dark:opacity-80 px-4"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            Contactez-nous pour discuter de vos besoins en communication et découvrir comment nos stratégies peuvent maximiser votre ROI et créer une mémorabilité pour vos messages publicitaires.
                        </motion.p>
                        <motion.div 
                            className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <Link 
                                to="/contact" 
                                className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-bold py-3 md:py-4 px-6 md:px-8 rounded-xl transition duration-300 text-sm md:text-base"
                            >
                                Commencer Maintenant
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </main>

            <Footer />
        </>
    )
}

export default Portfolio;
